import { Component, computed, effect, inject, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  Validators,
  ValidationErrors,
  AbstractControl
} from '@angular/forms';
import {
  GenericDropdownComponent,
  DropdownData,
  CurrencyInputFieldComponent,
  CurrencyFormatPipe
} from '@zayan-erp/shared';
import { toSignal } from '@angular/core/rxjs-interop';
import { rxResourceFixed } from '@zayan-erp/shared';
import { of } from 'rxjs';
import { map } from 'rxjs/operators';
import { ModelService } from '../../services/model.service';
import { ProductService } from '../../services/product.service';
import {
  ModelWithElevations,
  ProductSubDetailsSaveData,
  ProductSubDetailsUpdateEvent
} from '../../models/product-sub-details.models';
import { ModelElevationProduct } from '../../models/model-elevation-product.model';
import { DatePickerModule } from '@openng/optimus-ui/datepicker';
import { InputFieldComponent } from '@zayan-erp/shared';

@Component({
  selector: 'lib-product-sub-details',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    GenericDropdownComponent,
    CurrencyInputFieldComponent,
    CurrencyFormatPipe,
    DatePickerModule,
    InputFieldComponent
  ],
  templateUrl: './product-sub-details.component.html'
})
export class ProductSubDetailsComponent {
  #modelService = inject(ModelService);
  #productService = inject(ProductService);
  #fb = inject(FormBuilder);

  // Inputs & Outputs
  projectId = input.required<string>();
  productId = input.required<string>();
  isEdit = input<boolean>(false);
  isGlobal = input<boolean>(false);
  hasExistingData = input<boolean>(false);
  existingAssignments = input<ModelElevationProduct[]>([]);
  editItem = input<ModelElevationProduct | null>(null);
  defaultSalesDescription = input<string | null>(null);

  cancelClick = output<void>();
  saveClick = output<ProductSubDetailsSaveData>();
  updateClick = output<ProductSubDetailsUpdateEvent>();

  // Validator: valid when at least one selection is present
  readonly #requireSelection = (control: AbstractControl): ValidationErrors | null => {
    const selection = (control.value as string[] | null) ?? [];
    if (!selection.length) {
      return { required: true };
    }
    return selection.some(id => !!id) ? null : { required: true };
  };

  // Reactive form
  form = this.#fb.group({
    selectedElevationIds: this.#fb.control<string[] | null>([], [this.#requireSelection]),
    selectedProductTypeId: this.#fb.control<string | null>(null, [Validators.required]),
    quantity: this.#fb.control<number | null>(null, [Validators.required, Validators.min(0)]),
    unitPrice: this.#fb.control<number | null>(null, [Validators.required, Validators.min(0)]),
    retailCredit: this.#fb.control<number | null>(null, [Validators.min(0)]),
    effectiveDate: this.#fb.control<Date | null>(new Date()),
    overwriteExisting: this.#fb.control<boolean>(false),
    selectAllElevations: this.#fb.control<boolean>(false),
    overrideSalesDescription: this.#fb.control<boolean>(false),
    salesDescription: this.#fb.control<string | null>({ value: null, disabled: true })
  });

  // Form value as signal for reactive computations
  formValue = toSignal(this.form.valueChanges, { initialValue: this.form.getRawValue() });

  // Reactive elevation IDs tracking
  #selectedElevationIdsFromForm = toSignal(this.form.get('selectedElevationIds')!.valueChanges, {
    initialValue: [] as string[] | null
  });
  selectedElevationIds = computed(() => {
    const value = this.#selectedElevationIdsFromForm() ?? [];
    return value.filter(id => !!id);
  });

  #existingAssignmentsSummary = computed(() => {
    const assignments = this.existingAssignments() ?? [];
    const elevationIds = new Set<string>();
    let hasGlobalAssignment = false;

    assignments.forEach(item => {
      if (item.applyToAllModelsAndElevations || item.isAllModels || item.isAllElevations) {
        hasGlobalAssignment = true;
      }
      if (item.elevationId) {
        elevationIds.add(item.elevationId);
      }
      (item.assignments ?? []).forEach(assignment => {
        if (assignment.elevationId) {
          elevationIds.add(assignment.elevationId);
        }
      });
    });

    return {
      elevationIds,
      hasGlobalAssignment
    };
  });

  shouldShowOverwriteExisting = computed(() => {
    if (this.isEdit()) {
      return false;
    }
    if (!this.hasExistingData()) {
      return false;
    }

    const selected = this.selectedElevationIds();
    const selectAll = this.selectAllElevations();
    const { elevationIds, hasGlobalAssignment } = this.#existingAssignmentsSummary();

    if (hasGlobalAssignment) {
      return true;
    }

    if (selectAll) {
      return elevationIds.size > 0;
    }

    if (!selected.length) {
      return false;
    }

    return selected.some(id => elevationIds.has(id));
  });

  // Select-all checkbox tracking
  #selectAllElevationsFromForm = toSignal(this.form.get('selectAllElevations')!.valueChanges, {
    initialValue: this.form.get('selectAllElevations')!.value ?? false
  });
  selectAllElevations = computed(() => this.#selectAllElevationsFromForm() ?? false);

  // Models resource (still needed to get elevations)
  modelsByProjectResource = rxResourceFixed({
    params: () => ({ projectId: this.projectId() }),
    defaultValue: [],
    stream: ({ params }) => {
      if (!params.projectId) return of([] as ModelWithElevations[]);
      return this.#productService
        .readModelsByProject(params.projectId)
        .pipe(map((models: ModelWithElevations[]) => models));
    }
  });

  // Elevations computed display
  elevationsDisplay = computed(() => {
    const allData = this.modelsByProjectResource.value() ?? [];
    const isGlobal = this.isGlobal();

    // Get all elevations from all models with modelName
    const elevationsWithModel = allData.flatMap(m => (m.elevations ?? []).map(e => ({ ...e, modelName: m.modelName })));
    const uniqueById = new Map<string, (typeof elevationsWithModel)[0]>();
    elevationsWithModel.forEach(e => {
      if (e?.id && !uniqueById.has(e.id)) uniqueById.set(e.id, e);
    });

    const items = Array.from(uniqueById.values()).map(elevation => ({
      id: elevation.id,
      firstLabel: elevation.elevationCode || '',
      secondLabel: elevation.modelName || '',
      modelId: elevation.modelId || ''
    })) as DropdownData[];

    return items;
  });

  // Product Types
  productTypes = toSignal(this.#productService.readProductTypes(), { initialValue: [] as DropdownData[] });
  productTypesDisplay = computed(() => this.productTypes());

  // Calculated amount
  amount = computed(() => {
    const v = this.formValue();
    const qty = (v?.quantity ?? 0) as number;
    const price = (v?.unitPrice ?? 0) as number;
    return qty * price;
  });

  // Effect: Ensure select-all logic is enforced
  #syncSelectAllWithElevations = effect(() => {
    const selectAll = this.selectAllElevations();
    const elevations = this.elevationsDisplay();
    const allElevationIds = elevations.map(e => e.id);
    const elevationControl = this.form.get('selectedElevationIds')!;
    const current = (elevationControl.value ?? []) as string[];

    if (!allElevationIds.length) {
      if (current.length) {
        elevationControl.setValue([]);
      }
      return;
    }

    if (selectAll) {
      const needsUpdate =
        current.length !== allElevationIds.length || allElevationIds.some(id => !current.includes(id));
      if (needsUpdate) {
        elevationControl.setValue(allElevationIds);
      }
    } else {
      const filtered = current.filter(id => allElevationIds.includes(id));
      if (filtered.length !== current.length) {
        elevationControl.setValue(filtered);
      }
    }
  });

  // Effect: Auto-select all when product is global and lock checkbox
  #handleGlobalProduct = effect(() => {
    const checkboxControl = this.form.get('selectAllElevations')!;
    if (this.isGlobal()) {
      if (!this.selectAllElevations()) {
        checkboxControl.setValue(true);
      }
      if (!checkboxControl.disabled) {
        checkboxControl.disable({ emitEvent: false });
      }
    } else {
      if (checkboxControl.disabled) {
        checkboxControl.enable({ emitEvent: false });
      }
    }
  });

  // Effect: Disable elevation dropdown when "select all" is checked or in edit mode
  handleElevationDropdownState = effect(() => {
    const elevationControl = this.form.get('selectedElevationIds')!;
    const selectAll = this.selectAllElevations();
    const isEditMode = this.isEdit();
    const isGlobalProduct = this.isGlobal();

    // Elevation should be disabled when:
    // 1. Select all is checked
    // 2. In edit mode (handled separately, but we check here too)
    // 3. Product is global (selectAll is always true for global)
    const shouldBeDisabled = selectAll || isEditMode || isGlobalProduct;

    if (shouldBeDisabled && !elevationControl.disabled) {
      elevationControl.disable({ emitEvent: false });
    } else if (!shouldBeDisabled && elevationControl.disabled) {
      elevationControl.enable({ emitEvent: false });
    }
  });

  // Effect: Reset form for non-global products when component is shown
  resetFormForNonGlobalProduct = effect(() => {
    const isGlobal = this.isGlobal();
    const editItem = this.editItem();
    if (!isGlobal && !editItem) {
      const checkboxControl = this.form.get('selectAllElevations')!;
      const elevationControl = this.form.get('selectedElevationIds')!;

      // Reset select all elevations checkbox to false (emit event so signal updates)
      if (checkboxControl.value !== false) {
        checkboxControl.setValue(false);
      }

      // Reset elevation dropdown to empty
      if (elevationControl.value && (elevationControl.value as string[]).length > 0) {
        elevationControl.setValue([], { emitEvent: false });
      }
      // Note: elevation control enable/disable is handled by #handleElevationDropdownState
    }
  });

  // Effect: Populate form when editing
  populateFormForEdit = effect(() => {
    const editItem = this.editItem();
    if (editItem && this.isEdit()) {
      const productTypeId = editItem.productTypeId ?? editItem.productType?.id ?? null;
      const override = editItem.descriptionOverride ?? editItem.product?.descriptionOverride ?? false;
      const salesDescription = override
        ? (editItem.salesDescription ?? editItem.product?.salesDescription ?? '')
        : null;

      this.form.patchValue(
        {
          selectedProductTypeId: productTypeId,
          quantity: editItem.quantity ?? null,
          unitPrice: editItem.unitPrice ?? null,
          retailCredit: editItem.retailCredit ?? null,
          overrideSalesDescription: override,
          salesDescription,
          effectiveDate: null
        },
        { emitEvent: false }
      );
      this.#syncSalesDescriptionControl(override, salesDescription);

      // Disable fields that shouldn't be editable in edit mode
      const elevationControl = this.form.get('selectedElevationIds');
      const selectAllControl = this.form.get('selectAllElevations');

      elevationControl?.disable({ emitEvent: false });
      selectAllControl?.disable({ emitEvent: false });

      // Remove required validator from elevation field in edit mode since it's disabled
      elevationControl?.clearValidators();
      elevationControl?.updateValueAndValidity({ emitEvent: false });
    } else if (!this.isEdit()) {
      // Re-enable fields when not in edit mode
      const elevationControl = this.form.get('selectedElevationIds');
      const selectAllControl = this.form.get('selectAllElevations');

      elevationControl?.enable({ emitEvent: false });
      selectAllControl?.enable({ emitEvent: false });

      // Re-add required validator for elevation field
      elevationControl?.setValidators([this.#requireSelection]);
      elevationControl?.updateValueAndValidity({ emitEvent: false });
    }
  });

  onOverrideToggleChange() {
    this.#syncSalesDescriptionControl(this.form.get('overrideSalesDescription')!.value === true);
  }

  onCancel() {
    this.cancelClick.emit();
  }

  onSave() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.isEdit() && this.editItem()?.id) {
      // In edit mode, emit update event
      const formValue = this.form.getRawValue();
      this.updateClick.emit({
        id: this.editItem()!.id!,
        payload: {
          productTypeId: formValue.selectedProductTypeId!,
          quantity: formValue.quantity ?? 0,
          unitPrice: formValue.unitPrice ?? 0,
          retailCredit: formValue.retailCredit ?? 0,
          ...this.salesDescriptionPayload(formValue)
        }
      });
    } else {
      // Normal save mode
      const payload = this.#buildPayload();
      this.saveClick.emit(payload);
    }
  }

  #buildPayload(): ProductSubDetailsSaveData {
    const formValue = this.form.getRawValue();
    const rawElevationIds = formValue.selectedElevationIds ?? [];
    const elevationIds = rawElevationIds.filter(id => !!id);
    const applyToAllModelsAndElevations = formValue.selectAllElevations ?? false;
    const effectiveDate = formValue.effectiveDate;

    return {
      elevationIds: applyToAllModelsAndElevations ? [] : elevationIds,
      productTypeId: formValue.selectedProductTypeId,
      quantity: formValue.quantity ?? 0,
      unitPrice: formValue.unitPrice ?? 0,
      retailCredit: formValue.retailCredit ?? 0,
      effectiveDate: effectiveDate
        ? effectiveDate instanceof Date
          ? effectiveDate.toISOString()
          : (effectiveDate as string)
        : undefined,
      projectId: this.projectId(),
      productId: this.productId(),
      applyToAllModelsAndElevations,
      overwriteExisting: formValue.overwriteExisting ?? false,
      ...this.salesDescriptionPayload(formValue)
    };
  }

  #syncSalesDescriptionControl(enabled: boolean, value?: string | null) {
    const control = this.form.get('salesDescription')!;

    if (!enabled) {
      control.reset({ value: null, disabled: true }, { emitEvent: false });
      return;
    }

    control.enable({ emitEvent: false });

    if (value) {
      control.setValue(value, { emitEvent: false });
      return;
    }

    if (!control.value) {
      control.setValue(this.#findExistingOverride() ?? this.defaultSalesDescription() ?? '', { emitEvent: false });
    }
  }

  #findExistingOverride(): string | null {
    for (const item of this.existingAssignments() ?? []) {
      if (item.descriptionOverride && item.salesDescription) {
        return item.salesDescription;
      }
      if (item.product?.descriptionOverride && item.product.salesDescription) {
        return item.product.salesDescription;
      }
    }
    return null;
  }

  salesDescriptionPayload(formValue: ReturnType<typeof this.form.getRawValue>) {
    const override = formValue.overrideSalesDescription ?? false;
    return {
      salesDescription: override ? (formValue.salesDescription ?? '') : null,
      descriptionOverride: override
    };
  }
}
